import React, { useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";
import { classNames } from "primereact/utils";
import { ConsentPolicyType, UserPhoneCheckStatus } from "../../../enums";
import { AnimatedField } from "../../inputs/AnimatedField/AnimatedField";
import { DefaultPhoneInputField } from "../../forms/inputs/phoneField/DefaultPhoneInputField";
import { DefaultButton } from "../../button/defaultButton/DefaultButton";
import { nameof } from "ts-simple-nameof";
import { DefaultInputField } from "../../forms/inputs/InputField/DefaultInputField";
import { IfFalse, IfTrue, RenderWhen } from "../../../infrastructure/components/RenderWhen";
import { AppLink } from "../../button/link/Link";
import { GoogleLoginButton } from "../../button/googleLoginButton/GoogleLoginButton";
import { useAuthenticationForm } from "../../../infrastructure/hooks/useAuthenticationForm";
import { ILoginFormModel } from "../../../../components/authentication/models/loginFormModel";
import { AuthenticateConsents } from "../../../../components/authentication/components/AuthenticateConsents";
import { GoogleConsentNotice } from "../../../../components/authentication/components/GoogleConsentNotice";
import { PolicyDialog } from "../../../../components/authentication/components/PolicyDialog";
import { AppDialog } from "../../AppDialog/AppDialog";
import { IUserModel } from "../../../infrastructure/hooks/useAuthentication";
import { BirthDateField } from "../../forms/inputs/birthDateField/BirthDateField";
import { EmailVerificationStep } from "../EmailVerificationStep/EmailVerificationStep";
import { MessageBanner } from "../../common/MessageBanner/MessageBanner";
import styles from "./AuthenticationForm.module.scss";

interface IAuthenticationFormProps {
  onLoginSuccess?: (user: IUserModel) => void;
  onStatusChange?: (status: UserPhoneCheckStatus | undefined) => void;
  showToasts?: boolean;
  showGoogleLogin?: boolean;
  showPolicyDialog?: boolean;
  formClassName?: string;
  fieldsClassName?: string;
  buttonClassName?: string;
  googleButtonClassName?: string;
  compact?: boolean;
  returnUrl?: string;
  /** Where Google sends the user back to log them in; defaults to returnUrl. */
  googleReturnUrl?: string;
}

export const AuthenticationForm = ({
  onLoginSuccess,
  onStatusChange,
  showToasts = true,
  showGoogleLogin = true,
  showPolicyDialog = true,
  formClassName,
  fieldsClassName,
  buttonClassName,
  googleButtonClassName,
  compact = false,
  returnUrl,
  googleReturnUrl = returnUrl,
}: IAuthenticationFormProps) => {
  const {
    form,
    inputMaxWidth,
    status,
    maskedEmail,
    hasEmail,
    isForgottenPasswordMode,
    policies,
    isLoading,
    termsOfUseAvailable,
    isGoogleUser,
    selectedStudio,
    askForUserBirthdayOnRegistration,
    isGoogleLoginDisabled,
    onSave,
    onForgottenPasswordClick,
    resetForm,
    handleVerifiedUser,
  } = useAuthenticationForm({
    onSuccessfulLogin: onLoginSuccess,
    showToasts,
    returnUrl,
  });

  const [showPolicyType, setShowPolicyType] = useState<ConsentPolicyType>();

  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  if (status === UserPhoneCheckStatus.PendingVerification) {
    return (
      <EmailVerificationStep
        maskedEmail={maskedEmail}
        phoneNumber={form.getValues("phoneNumber")}
        onVerified={handleVerifiedUser}
        onBack={resetForm}
      />
    );
  }

  return (
    <>
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSave)}
          className={classNames(styles.form, formClassName)}
        >
          <div className={classNames(styles.fields, fieldsClassName)}>
            <AnimatedField>
              <DefaultPhoneInputField
                label="Телефонен номер"
                name={nameof<ILoginFormModel>((x) => x.phoneNumber)}
                width={inputMaxWidth}
                required
              />
            </AnimatedField>
          </div>

          {/* BrandNew: email, name, birth date, password, confirm password, consents */}
          <RenderWhen condition={status === UserPhoneCheckStatus.BrandNew}>
            <div className={classNames(styles.fields, fieldsClassName)}>
              <AnimatedField>
                <DefaultInputField
                  name={nameof<ILoginFormModel>((x) => x.email)}
                  placeholder={"email@example.com"}
                  label="Имейл адрес"
                  width={inputMaxWidth}
                  required
                />
              </AnimatedField>

              <AnimatedField>
                <DefaultInputField
                  name={nameof<ILoginFormModel>((x) => x.firstName)}
                  placeholder={"Иван"}
                  label="Малко име"
                  width={inputMaxWidth}
                  required
                />
              </AnimatedField>

              <AnimatedField>
                <DefaultInputField
                  name={nameof<ILoginFormModel>((x) => x.lastName)}
                  placeholder={"Иванов"}
                  label="Фамилно име"
                  width={inputMaxWidth}
                  required
                />
              </AnimatedField>

              <RenderWhen condition={askForUserBirthdayOnRegistration}>
                <AnimatedField>
                  <BirthDateField
                    name={nameof<ILoginFormModel>((x) => x.birthDate)}
                    label="Дата на раждане"
                    width={inputMaxWidth}
                  />
                </AnimatedField>
              </RenderWhen>

              <AnimatedField>
                <DefaultInputField
                  name={nameof<ILoginFormModel>((x) => x.password)}
                  type="password"
                  label="Парола"
                  placeholder={"*******"}
                  width={inputMaxWidth}
                  required
                />
              </AnimatedField>

              <AnimatedField>
                <DefaultInputField
                  name={nameof<ILoginFormModel>((x) => x.confirmPassword)}
                  type="password"
                  label="Потвърди парола"
                  placeholder={"*******"}
                  width={inputMaxWidth}
                  required
                />
              </AnimatedField>

              <RenderWhen condition={!isGoogleUser}>
                <div className={styles.consents}>
                  <AuthenticateConsents
                    onPolicyClick={
                      showPolicyDialog ? setShowPolicyType : undefined
                    }
                    isTermsOfUseAvailable={termsOfUseAvailable}
                  />
                </div>
              </RenderWhen>
            </div>
          </RenderWhen>

          {/* FirstLogin: first-login notice, optional email, password, confirm password */}
          <RenderWhen condition={status === UserPhoneCheckStatus.FirstLogin}>
            <div className={classNames(styles.fields, fieldsClassName)}>
              {/* Up front, before the fields: at the bottom it went unread and people did not
                  get why a login asks them to set a password */}
              <AnimatedField>
                <MessageBanner
                  type="info"
                  icon="pi pi-key"
                  title="Влизате за първи път"
                  message={
                    hasEmail
                      ? "Профилът ви вече е създаден. Задайте парола, с която ще влизате занапред."
                      : "Профилът ви вече е създаден. Добавете имейл и задайте парола, с която ще влизате занапред."
                  }
                  width={inputMaxWidth}
                />
              </AnimatedField>

              <RenderWhen condition={!hasEmail}>
                <AnimatedField>
                  <DefaultInputField
                    name={nameof<ILoginFormModel>((x) => x.email)}
                    placeholder={"email@example.com"}
                    label="Имейл адрес"
                    width={inputMaxWidth}
                    required
                    autofocus
                  />
                </AnimatedField>
              </RenderWhen>

              <AnimatedField>
                <DefaultInputField
                  name={nameof<ILoginFormModel>((x) => x.password)}
                  type="password"
                  label="Парола"
                  placeholder={"*******"}
                  width={inputMaxWidth}
                  required
                  autofocus={hasEmail}
                />
              </AnimatedField>

              <AnimatedField>
                <DefaultInputField
                  name={nameof<ILoginFormModel>((x) => x.confirmPassword)}
                  type="password"
                  label="Потвърди парола"
                  placeholder={"*******"}
                  width={inputMaxWidth}
                  required
                />
              </AnimatedField>

              <RenderWhen condition={!isGoogleUser}>
                <div className={styles.consents}>
                  <AuthenticateConsents
                    onPolicyClick={
                      showPolicyDialog ? setShowPolicyType : undefined
                    }
                    isTermsOfUseAvailable={termsOfUseAvailable}
                  />
                </div>
              </RenderWhen>
            </div>
          </RenderWhen>

          {/* ForgottenPassword: email */}
          {isForgottenPasswordMode && (
            <div className={classNames(styles.fields, fieldsClassName)}>
              <AnimatedField>
                <DefaultInputField
                  name={nameof<ILoginFormModel>((x) => x.email)}
                  placeholder={"email@example.com"}
                  label="Имейл адрес"
                  width={inputMaxWidth}
                  required
                />
              </AnimatedField>
              <AnimatedField>
                <p className={styles.infoText}>
                  На имейлът ще получите линк за възстановяване на паролата.
                </p>
              </AnimatedField>
              <AnimatedField>
                <DefaultButton
                  className={classNames(styles.button, buttonClassName)}
                  label={"Напред"}
                  design="accent"
                  type="submit"
                  loading={isLoading}
                />
              </AnimatedField>
            </div>
          )}

          {/* Regular: password + forgotten password link */}
          <RenderWhen condition={status === UserPhoneCheckStatus.Regular && !isForgottenPasswordMode}>
            <AnimatedField>
              <DefaultInputField
                name={nameof<ILoginFormModel>((x) => x.password)}
                type="password"
                label="Парола"
                placeholder={"*******"}
                width={inputMaxWidth}
                autofocus
              />
            </AnimatedField>

            <div className={styles.forgotPasswordLink}>
              <AppLink
                className={styles.link}
                label="Забравена парола?"
                onClick={onForgottenPasswordClick}
              />
            </div>
          </RenderWhen>

          {/* Submit button */}
          <RenderWhen condition={status === UserPhoneCheckStatus.Regular && !isForgottenPasswordMode}>
            <IfTrue>
              <AnimatedField>
                <DefaultButton
                  className={classNames(styles.button, buttonClassName)}
                  label={"Вход"}
                  design="accent"
                  type="submit"
                  loading={isLoading}
                />
              </AnimatedField>
            </IfTrue>
            <IfFalse>
              {!isForgottenPasswordMode && (
                <AnimatedField>
                  <DefaultButton
                    className={classNames(styles.button, buttonClassName)}
                    label={
                      status === UserPhoneCheckStatus.BrandNew ||
                      status === UserPhoneCheckStatus.FirstLogin
                        ? "Продължи"
                        : "Напред"
                    }
                    design="accent"
                    type="submit"
                    loading={isLoading || !selectedStudio}
                  />
                </AnimatedField>
              )}
            </IfFalse>
          </RenderWhen>
        </form>

        {/* Google Login Section */}
        {showGoogleLogin && !isGoogleLoginDisabled && (
          <RenderWhen condition={!status && !isForgottenPasswordMode}>
            <div className={styles.googleLoginSection}>
              <div className={styles.divider}>
                <span className={styles.dividerText}>или</span>
              </div>
              <GoogleLoginButton
                className={classNames(
                  styles.googleButton,
                  googleButtonClassName,
                )}
                text="Вход с Google"
                returnUrl={googleReturnUrl}
              />
              {!compact && (
                <GoogleConsentNotice
                  onPolicyClick={
                    showPolicyDialog ? setShowPolicyType : undefined
                  }
                  isTermsOfUseAvailable={termsOfUseAvailable}
                />
              )}
            </div>
          </RenderWhen>
        )}
      </FormProvider>

      {showPolicyDialog && (
        <AppDialog
          visible={!!showPolicyType}
          onHide={() => setShowPolicyType(undefined)}
          showHeader={false}
          className={styles.dialog}
          contentClassName={styles.dialogContent}
        >
          <PolicyDialog
            allPolicies={policies}
            selectedPolicy={showPolicyType}
            onClose={() => setShowPolicyType(undefined)}
          />
        </AppDialog>
      )}
    </>
  );
};
